/** 素材模板（商品/尺码/属性/实拍）JSON 导入导出 */

export const MATERIAL_TEMPLATE_SCHEMA = 'temu-material-templates/v1';

export const MATERIAL_TEMPLATE_TYPES = {
  products: 'products',
  sizeCharts: 'size-charts',
  productAttributes: 'product-attributes',
  photos: 'photos',
  certificates: 'certificates',
  models: 'models',
};

const TYPE_LABELS = {
  [MATERIAL_TEMPLATE_TYPES.products]: '产品模板',
  [MATERIAL_TEMPLATE_TYPES.sizeCharts]: '尺码库',
  [MATERIAL_TEMPLATE_TYPES.productAttributes]: '属性库',
  [MATERIAL_TEMPLATE_TYPES.photos]: '实拍图库',
  [MATERIAL_TEMPLATE_TYPES.certificates]: '证书库',
  [MATERIAL_TEMPLATE_TYPES.models]: '模特库',
};

export function materialTemplateTypeLabel(type) {
  return TYPE_LABELS[type] || '素材模板';
}

function asText(value) {
  if (value == null) return '';
  return String(value).trim();
}

function asBool(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

/** 列表/详情 → 可移植条目（去掉 id / version / 审计字段） */
export function toPortableTemplateItem(item, type) {
  const payload = item?.payload && typeof item.payload === 'object'
    ? item.payload
    : {};
  const out = {
    name: asText(item?.name) || '未命名模板',
    remark: asText(item?.remark),
    productTypeId: asText(item?.productTypeId),
    categoryId: asText(item?.categoryId),
    categoryPathLabel: asText(item?.categoryPathLabel),
    autoRuleEnabled: asBool(item?.autoRuleEnabled),
    payload,
  };
  if (type === MATERIAL_TEMPLATE_TYPES.photos) {
    out.spuName = asText(item?.spuName);
    out.spuTitle = asText(item?.spuTitle);
  }
  if (type === MATERIAL_TEMPLATE_TYPES.sizeCharts) {
    const fromItem = asText(item?.sizeSaleAttributeId);
    const fromPayload = asText(payload?.sizeSaleAttributeId);
    out.sizeSaleAttributeId = fromItem || fromPayload;
  }
  if (type === MATERIAL_TEMPLATE_TYPES.certificates) {
    out.certificateTypeId = item?.certificateTypeId ?? payload?.certificateTypeId ?? null;
    out.certificateTypeName = asText(item?.certificateTypeName || payload?.certificateTypeName);
    out.certificateTypeCode = asText(item?.certificateTypeCode || payload?.certificateTypeCode);
    out.fileName = asText(item?.fileName || payload?.fileName);
  }
  if (type === MATERIAL_TEMPLATE_TYPES.models) {
    out.modelName = asText(item?.modelName || payload?.modelName || item?.name);
    out.modelType = item?.modelType ?? payload?.modelType ?? 0;
    out.headPortrait = asText(item?.headPortrait || payload?.headPortrait);
  }
  return out;
}

export function buildMaterialTemplateExportBundle({
  type,
  shopId,
  items = [],
} = {}) {
  const list = (Array.isArray(items) ? items : [])
    .map((item) => toPortableTemplateItem(item, type));
  return {
    schema: MATERIAL_TEMPLATE_SCHEMA,
    type,
    exportedAt: new Date().toISOString(),
    sourceShopId: shopId != null ? String(shopId) : '',
    items: list,
  };
}

export function parseMaterialTemplateImportBundle(raw, expectedType) {
  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      throw new Error('JSON 解析失败，请确认文件格式');
    }
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('文件内容无效');
  }
  if (data.schema !== MATERIAL_TEMPLATE_SCHEMA) {
    throw new Error('不是受支持的素材模板导入文件');
  }
  const type = String(data.type || '').trim();
  if (!Object.values(MATERIAL_TEMPLATE_TYPES).includes(type)) {
    throw new Error('未知的模板类型');
  }
  if (expectedType && type !== expectedType) {
    throw new Error(
      `文件类型不匹配（当前页为${materialTemplateTypeLabel(expectedType)}，文件为${materialTemplateTypeLabel(type)}）`,
    );
  }
  const items = Array.isArray(data.items) ? data.items : null;
  if (!items || !items.length) {
    throw new Error('文件中没有可导入的模板');
  }
  return {
    schema: MATERIAL_TEMPLATE_SCHEMA,
    type,
    exportedAt: data.exportedAt || '',
    sourceShopId: data.sourceShopId != null ? String(data.sourceShopId) : '',
    items: items.map((item) => toPortableTemplateItem(item, type)),
  };
}

export function downloadJson(data, filename) {
  const blob = new Blob([`${JSON.stringify(data, null, 2)}\n`], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** 弹出文件选择并读取 JSON */
export function pickJsonFile() {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.style.display = 'none';
    let settled = false;
    const cleanup = () => {
      window.removeEventListener('focus', onWindowFocus);
      if (input.parentNode) input.parentNode.removeChild(input);
    };
    const fail = (err) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err instanceof Error ? err : new Error(String(err || '未选择文件')));
    };
    const succeed = (value) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };
    const onWindowFocus = () => {
      // 取消选择时通常不会触发 change；延迟后检查
      window.setTimeout(() => {
        if (!settled && !input.files?.length) {
          fail(new Error('未选择文件'));
        }
      }, 300);
    };
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        fail(new Error('未选择文件'));
        return;
      }
      try {
        const text = await file.text();
        succeed({ file, text, data: JSON.parse(text) });
      } catch (e) {
        fail(new Error(e?.message || 'JSON 解析失败'));
      }
    };
    document.body.appendChild(input);
    window.addEventListener('focus', onWindowFocus);
    input.click();
  });
}

export async function fetchTemplatesForExport(rows, getOne) {
  const list = Array.isArray(rows) ? rows : [];
  const details = [];
  for (const row of list) {
    const id = row?.id;
    if (id == null || id === '') {
      throw new Error('选中记录缺少 id，无法导出完整内容');
    }
    const res = await getOne(id);
    const item = res?.item || res || {};
    details.push(item);
  }
  return details;
}

function typeKey(productTypeId) {
  const id = asText(productTypeId);
  return id || '__none__';
}

function takeNextPriority(used) {
  let p = 0;
  while (used.has(p)) p += 1;
  used.add(p);
  return p;
}

/**
 * 按条目创建模板；同类目优先级自动避让。
 * @returns {{ ok: number, failed: number, errors: Array<{index,name,message}> }}
 */
export async function importMaterialTemplates({
  type,
  items = [],
  listByProductType,
  createOne,
  onProgress,
} = {}) {
  const rows = Array.isArray(items) ? items : [];
  const usedByType = new Map();

  const ensureUsed = async (productTypeId) => {
    const key = typeKey(productTypeId);
    if (usedByType.has(key)) return usedByType.get(key);
    const existing = typeof listByProductType === 'function'
      ? await listByProductType(productTypeId || undefined)
      : [];
    const used = new Set();
    (Array.isArray(existing) ? existing : []).forEach((item) => {
      const n = Number(item?.sortOrder);
      if (Number.isFinite(n)) used.add(n);
    });
    usedByType.set(key, used);
    return used;
  };

  let ok = 0;
  const errors = [];
  for (let i = 0; i < rows.length; i += 1) {
    const item = rows[i];
    try {
      const used = await ensureUsed(item.productTypeId);
      const sortOrder = takeNextPriority(used);
      const body = {
        name: item.name,
        remark: item.remark || undefined,
        productTypeId: item.productTypeId || undefined,
        categoryId: item.categoryId || undefined,
        categoryPathLabel: item.categoryPathLabel || undefined,
        autoRuleEnabled: !!item.autoRuleEnabled,
        sortOrder,
        payload: item.payload && typeof item.payload === 'object' ? item.payload : {},
      };
      if (type === MATERIAL_TEMPLATE_TYPES.sizeCharts && item.sizeSaleAttributeId) {
        body.sizeSaleAttributeId = item.sizeSaleAttributeId;
      }
      if (type === MATERIAL_TEMPLATE_TYPES.photos) {
        if (item.spuName) body.spuName = item.spuName;
        if (item.spuTitle) body.spuTitle = item.spuTitle;
      }
      await createOne(body);
      ok += 1;
    } catch (e) {
      errors.push({
        index: i,
        name: item?.name || `第 ${i + 1} 条`,
        message: e?.message || '导入失败',
      });
    }
    onProgress?.(i + 1, rows.length);
  }
  return { ok, failed: errors.length, errors };
}

/** 从对象数组导出 CSV（兼容模板列表页旧调用习惯） */
export function downloadCsvFromObjects(rows, filename) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return;
  const headers = Object.keys(list[0] || {});
  const escape = (value) => {
    const text = value == null ? '' : String(value);
    if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };
  const lines = [headers.join(',')];
  list.forEach((row) => {
    lines.push(headers.map((key) => escape(row?.[key])).join(','));
  });
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
